import { config } from '../config';
import { DEFAULT_LOCALE, extractLanguage } from './lang/lang';
import langReducer from './lang/reducer';
import { getLanguageData, getLocale } from './lang/selectors';
import { configureFetchLanguageData } from './lang/services';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/actions';
import { configureMockUserManager, configureUserManager } from './login/login';
import handleLoginRedirect from './login/redirect';
import loginReducer from './login/reducer';
import { isUserSessionExpired } from './login/selectors';
import configReducer from './setup/configReducer';
import { reportErrorToSentry } from './setup/sentry.js';
import { store } from './setup/store';
import { trace } from './setup/trace';
import { accessToken } from './tokenHandling/accessToken';
import { accessTokenStored, idTokenStored } from './tokenHandling/actions';
import tokenHandlingReducer from './tokenHandling/reducer';
import { getAccessToken, getIdToken } from './tokenHandling/selectors';

function main(userManagerInput) {
    const fetchLanguageData = configureFetchLanguageData(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState()));
        const html = document.querySelector('html');

        if (html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });

    const oauthConfig = {
        onTokenExpired: () => {
            trace('index.onTokenExpired');

            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onTokenRenewed: (result) => {
            trace('index.onTokenRenewed', result);

            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));

            store.dispatch(userSessionRenewed());
            // You will need to get the user language by yourself then
            // you may fetch the suitable messages from the CDN. Depending
            // on when and from where you fetch the user settings you might
            // want to employ a loading spinner while the request is ongoing.
            fetchLanguageData(result.locale);
        },
    };

    const isAllowedToMockAuth = !import.meta.env.PROD;
    const testAccessToken = sessionStorage?.getItem('test_access_token'); // enables mocking of authentication in production
    const testIdToken = sessionStorage?.getItem('test_id_token'); // enables mocking of authentication in production
    const userManager =
        (isAllowedToMockAuth && config.login.mockAuthorization) || testAccessToken
            ? configureMockUserManager(oauthConfig, { accessToken: testAccessToken, idToken: testIdToken })
            : configureUserManager(oauthConfig, userManagerInput);

    if (!config.isVolkswagenBuild) {
        import('@rio-cloud/rio-user-menu-component').then((module) => {
            document.addEventListener(module.EVENT_USER_LANGUAGE_CHANGED, userManager.signinSilent.bind(userManager));
            document.addEventListener(module.EVENT_USER_PROFILE_CHANGED, userManager.signinSilent.bind(userManager));
            document.addEventListener(module.EVENT_USER_LOGGED_OUT, userManager.signoutRedirect.bind(userManager));
        });
    }

    try {
        userManager.clearStaleState().catch((error) => reportErrorToSentry(error));
    } catch (error) {
        reportErrorToSentry(error);
    }
    return userManager;
}

export {
    configReducer,
    DEFAULT_LOCALE,
    getAccessToken,
    getIdToken,
    getLanguageData,
    getLocale,
    handleLoginRedirect,
    isUserSessionExpired,
    langReducer,
    loginReducer,
    main,
    store,
    tokenHandlingReducer,
};
